import { HOME_GEOFENCE_METERS, ORIGIN_PRESETS, stationForHome, type OriginPreset } from "../shared/origins";
import { morningProfileForStation } from "./profiles";
import { distanceMeters } from "../shared/time";

export interface HomeFix {
	lat: number;
	lon: number;
	accuracyMeters: number;
	fixAtMs: number;
}

export type HomeEvidence = { kind: "inside"; home: OriginPreset } | { kind: "outside" } | { kind: "ambiguous" };

export const FOLLOWER_HOME_FRESH_MS = 2 * 60_000;

/** The position out of a `native-fix` diagnostic, or null when the shell sent something else. */
export function readHomeFix(detail: unknown): HomeFix | null {
	if (!detail || typeof detail !== "object") {
		return null;
	}
	const value = detail as Record<string, unknown>;
	return typeof value.lat === "number" &&
		typeof value.lon === "number" &&
		typeof value.accuracyMeters === "number" &&
		typeof value.fixAtMs === "number"
		? { lat: value.lat, lon: value.lon, accuracyMeters: value.accuracyMeters, fixAtMs: value.fixAtMs }
		: null;
}

/**
 * A home claim is only sound when the entire accuracy circle fits inside its geofence.
 *
 * `fixAtMs` is optional because a page diagnostic carries a position without one, and where the
 * phone is does not depend on when it noticed.
 */
export function homeEvidence(fix: { lat: number; lon: number; accuracyMeters: number; fixAtMs?: number }): HomeEvidence {
	if (!Number.isFinite(fix.accuracyMeters) || fix.accuracyMeters < 0) {
		return { kind: "ambiguous" };
	}
	const distances = ORIGIN_PRESETS.map((home) => ({ home, meters: distanceMeters(home, fix) }));
	const inside = distances.find(({ meters }) => meters + fix.accuracyMeters <= HOME_GEOFENCE_METERS);
	if (inside) {
		return { kind: "inside", home: inside.home };
	}
	if (distances.every(({ meters }) => meters - fix.accuracyMeters > HOME_GEOFENCE_METERS)) {
		return { kind: "outside" };
	}
	return { kind: "ambiguous" };
}

export function followerIsAtHome(
	homeId: string | null,
	seenAtMs: number | null,
	arrivalHomeId: string,
	nowMs: number,
): boolean {
	return homeId === arrivalHomeId && seenAtMs != null && nowMs - seenAtMs <= FOLLOWER_HOME_FRESH_MS;
}

/**
 * The morning that leaves from this house.
 *
 * Which house he sleeps at and which commute he leaves on are one fact, so arriving is the moment
 * it stops being a setting somebody has to remember to change.
 */
export function morningCommuteAtHome(home: OriginPreset): { profileId: string; origin: { lat: number; lon: number } } | null {
	const profile = morningProfileForStation(stationForHome(home.id));
	return profile ? { profileId: profile.id, origin: { lat: home.lat, lon: home.lon } } : null;
}